import { usePersistedState } from "@/lib/persistedFilters";
// SIP Trunks — staff-only inventory of every SIP trunk on the UC PBX.
// Phase 1 is CSV-import driven; Phase 2 (backlog) will wire live sync from
// the UC PBX API and upsert on the (partner, domain) natural key.
//
// UX intentionally mirrors the Hardware page (same hub) but simplified —
// only 3 core columns to start (Partner, Domain, Call limit).

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Radio, Plus, Search, Pencil, Trash2, Loader2, Upload, Download, X, Save,
  Link as LinkIcon, FileDown,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useTablePagination, TablePagination, PageSizeSelect } from "@/components/TablePagination";

const EMPTY = () => ({ partner: "", domain: "", call_limit_ext: 0, notes: "" });

export default function SipTrunks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = usePersistedState("sip-trunks.q", "", searchParams.get("q"));
  const [partnerFilter, setPartnerFilter] = usePersistedState("sip-trunks.partner", "all", searchParams.get("partner"));
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    const next = new URLSearchParams();
    if (q.trim())              next.set("q", q.trim());
    if (partnerFilter !== "all") next.set("partner", partnerFilter);
    setSearchParams(next, { replace: true });
  }, [q, partnerFilter, setSearchParams]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/sip-trunks?limit=20000"),
        api.get("/sip-trunks/stats"),
      ]);
      setRows(list.items || []); setStats(s);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load SIP trunks");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const partnerOpts = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.partner && s.add(r.partner));
    return [...s].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    let out = rows;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((r) => [r.partner, r.domain, r.notes]
        .some((v) => (v || "").toLowerCase().includes(s)));
    }
    if (partnerFilter !== "all") out = out.filter((r) => r.partner === partnerFilter);
    return out;
  }, [rows, q, partnerFilter]);

  const pg = useTablePagination(filtered, 50);

  const del = async (row) => {
    if (!window.confirm(`Delete trunk ${row.partner}/${row.domain}?`)) return;
    try {
      await api.delete(`/sip-trunks/${row.trunk_id}`);
      toast.success("Deleted"); await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (q.trim())               params.set("q", q.trim());
    if (partnerFilter !== "all") params.set("partner", partnerFilter);
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sip-trunks/export.csv?${params}`, "_blank");
    toast.success("Export started");
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6" data-testid="sip-trunks-page">
      <PageHeader testid="sip-trunks-header" title="SIP Trunks" icon={Radio}
        subtitle="Every SIP trunk configured on the UC PBX, indexed by partner and domain.">
        <Button variant="outline" onClick={exportCsv} data-testid="sip-trunks-export"
                disabled={filtered.length === 0}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
        <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="sip-trunks-bulk">
          <Upload className="h-4 w-4" /> Bulk upload
        </Button>
        <Button onClick={() => setEditing("new")} data-testid="sip-trunks-add"
                className="bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus className="h-4 w-4" /> Add trunk
        </Button>
      </PageHeader>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <StatCard label="Total trunks"    value={stats.total_trunks.toLocaleString()} icon={Radio} />
          <StatCard label="Partners"        value={stats.partners.toLocaleString()}     icon={LinkIcon} />
          <StatCard label="Total capacity"  value={stats.total_capacity.toLocaleString() + " ext"} icon={Radio} />
        </div>
      )}

      <Card className="border-slate-200 shadow-sm p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search partner, domain, notes…"
                   className="pl-7 h-9" data-testid="sip-trunks-search" />
          </div>
          <div className="flex items-center gap-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Partner</Label>
            <Select value={partnerFilter} onValueChange={setPartnerFilter}>
              <SelectTrigger className="h-9 min-w-[160px]" data-testid="sip-trunks-filter-partner">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                {partnerOpts.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <PageSizeSelect pageSize={pg.pageSize} setPageSize={pg.setPageSize} testid="sip-trunks-page-size" />
          <span className="text-xs text-slate-400 tabular-nums" data-testid="sip-trunks-count">
            {filtered.length.toLocaleString()} of {rows.length.toLocaleString()}
          </span>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Partner</th>
                <th className="px-3 py-2 text-left">Domain</th>
                <th className="px-3 py-2 text-right">Call limit</th>
                <th className="px-3 py-2 text-left">Notes</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
              ) : pg.pageItems.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-12 text-center">
                  <Radio className="mx-auto h-8 w-8 text-slate-300 mb-2" />
                  <div className="text-sm font-semibold text-slate-600">No SIP trunks</div>
                  <div className="mt-1 text-xs text-slate-400">
                    Click <strong>Bulk upload</strong> to import from a UC PBX export.
                  </div>
                </td></tr>
              ) : pg.pageItems.map((r) => (
                <tr key={r.trunk_id} data-testid={`sip-trunks-row-${r.trunk_id}`}
                    className="hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-slate-700">{r.partner}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{r.domain}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{r.call_limit_ext}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[300px]">{r.notes || "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(r)} data-testid={`sip-trunks-edit-${r.trunk_id}`}
                      className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(r)} data-testid={`sip-trunks-delete-${r.trunk_id}`}
                      className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePagination {...pg} testid="sip-trunks-pagination" />
      </Card>

      {editing && <TrunkDialog editing={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {bulkOpen && <BulkDialog onClose={() => setBulkOpen(false)} onDone={load} />}
    </div>
  );
}


function StatCard({ label, value, icon: Icon }) {
  return (
    <Card className="border-slate-200 shadow-sm p-3 flex items-center gap-3">
      <div className="rounded-lg bg-primary/10 p-2 text-primary"><Icon className="h-4 w-4" /></div>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        <div className="text-lg font-semibold text-slate-800 tabular-nums">{value}</div>
      </div>
    </Card>
  );
}


// ---- Add / Edit dialog -----------------------------------------------

function TrunkDialog({ editing, onClose, onSaved }) {
  const creating = editing === "new";
  const [f, setF] = useState(creating ? EMPTY() : { ...EMPTY(), ...editing });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((cur) => ({ ...cur, ...patch }));

  const submit = async () => {
    if (!f.partner?.trim() || !f.domain?.trim()) {
      toast.error("Partner and domain are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...f,
        call_limit_ext: parseInt(f.call_limit_ext, 10) || 0,
      };
      if (creating) {
        await api.post("/sip-trunks", payload);
        toast.success("Trunk added");
      } else {
        await api.patch(`/sip-trunks/${editing.trunk_id}`, payload);
        toast.success("Saved");
      }
      onSaved(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sip-trunks-dialog">
        <DialogHeader>
          <DialogTitle>{creating ? "Add SIP trunk" : `Edit ${editing.partner}/${editing.domain}`}</DialogTitle>
          <DialogDescription>
            Partner (territory) + domain are the natural key — the pair must be unique.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div>
            <Label className="text-[11px] text-slate-500 mb-1 block">Partner (territory) *</Label>
            <Input value={f.partner} onChange={(e) => set({ partner: e.target.value })}
                   placeholder="..altismsp.com" data-testid="sip-field-partner" />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500 mb-1 block">Domain *</Label>
            <Input value={f.domain} onChange={(e) => set({ domain: e.target.value })}
                   placeholder="altis.msp" data-testid="sip-field-domain" />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500 mb-1 block">Call limit (concurrent ext)</Label>
            <Input type="number" min="0" value={f.call_limit_ext}
                   onChange={(e) => set({ call_limit_ext: e.target.value })}
                   data-testid="sip-field-limit" />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] text-slate-500 mb-1 block">Notes</Label>
            <Textarea rows={3} value={f.notes} onChange={(e) => set({ notes: e.target.value })}
                      placeholder="Peering carrier, special routing, contract expiry…"
                      data-testid="sip-field-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="sip-cancel"><X className="h-4 w-4" /> Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="sip-submit"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---- Bulk CSV upload -------------------------------------------------

function BulkDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);
  // Iter 88p — mirror the Reports.jsx re-upload UX.
  const [mode, setMode] = useState("add");
  const inputRef = useRef(null);

  const doUpload = async () => {
    if (!file) { toast.error("Pick a CSV first"); return; }
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/sip-trunks/bulk-upload?mode=${mode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) toast.success(`Imported ${data.created} trunks`);
      if (data.replaced > 0) toast.info(`${data.replaced} existing trunks soft-deleted`);
      if (data.total_errors) toast.warning(`${data.total_errors} rows failed`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer?.files?.[0]; if (f) setFile(f);
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="sip-trunks-bulk-dialog">
        <DialogHeader>
          <DialogTitle>Bulk upload SIP trunks</DialogTitle>
          <DialogDescription>
            Import from the UC PBX <code className="text-xs">all_sip_domains</code>
            {" "}CSV. Duplicate (partner, domain) rows are skipped — safe to re-run.
            Column names <code className="text-xs">territory</code>, <code className="text-xs">domain</code>, <code className="text-xs">call_limit_ext</code> are recognised.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
              dragging ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"
            }`}
            data-testid="sip-trunks-bulk-dropzone">
            <Upload className="mx-auto h-6 w-6 text-slate-400 mb-1" />
            {file ? (
              <div className="text-sm text-slate-700 font-medium">{file.name}
                <span className="ml-2 text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            ) : (
              <>
                <div className="text-sm text-slate-600">Drop CSV here, or click to browse</div>
                <div className="text-[11px] text-slate-400 mt-0.5">UTF-8 encoded, header row required</div>
              </>
            )}
            <input ref={inputRef} type="file" accept=".csv,text/csv"
                   onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden"
                   data-testid="sip-trunks-bulk-file" />
          </div>

          {/* Iter 88p — mode selector. Default: append (safe). Replace wipes
              all existing trunks (soft-delete) before importing the new file. */}
          <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="min-w-[140px] space-y-1">
              <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Upload mode
              </Label>
              <Select value={mode} onValueChange={setMode} disabled={uploading}>
                <SelectTrigger className="h-8 text-xs"
                               data-testid="sip-trunks-bulk-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add" data-testid="sip-trunks-bulk-mode-add">
                    Add new (append)
                  </SelectItem>
                  <SelectItem value="replace" data-testid="sip-trunks-bulk-mode-replace">
                    Replace all
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-[11px] text-slate-600 leading-relaxed"
                 data-testid="sip-trunks-bulk-mode-hint">
              {mode === "add"
                ? "Existing trunks stay put. Only brand-new (partner, domain) rows are inserted; duplicates are skipped."
                : <><strong className="text-rose-700">Replace all</strong> soft-deletes every existing SIP trunk first, then imports the CSV as the new source of truth. Use this only for a full refresh from UC PBX.</>}
            </div>
          </div>
          {result && (
            <div className="rounded-lg border border-slate-200 p-3 text-xs space-y-1" data-testid="sip-trunks-bulk-result">
              <div className="flex gap-4">
                <span className="text-emerald-700 font-semibold">✓ Created: {result.created}</span>
                <span className="text-slate-600">Skipped: {result.skipped_duplicates}</span>
                <span className="text-rose-700">Errors: {result.total_errors || 0}</span>
              </div>
              {(result.errors || []).length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5">
                  {result.errors.map((er, i) => (
                    <div key={i} className="text-[11px] text-rose-700 font-mono">
                      row {er.row} — {er.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="sip-trunks-bulk-cancel">
            {result ? "Close" : "Cancel"}
          </Button>
          <Button onClick={doUpload} disabled={!file || uploading}
                  data-testid="sip-trunks-bulk-submit"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Silence unused-import warning for FileDown (icon reserved for future template button).
void FileDown;
